import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  GET_ALL_ADDRESS,
  UPDATE_ADDRESS_STATUS,
} from "../../services/Endpoints";
import {
  ADDRESS_STATUS_LABEL,
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  sweetAlertError,
  toastSuccess,
} from "../../utils/AppFunctions";
import { ADDRESS_STATUS } from "../../utils/AppEnumerations";

export interface IAddress {
  id: string;
  firm_name: string;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  city: string;
  state_name: string;
  country_name: string;
  zip_code: string;
  status: ADDRESS_STATUS;
  reason: string;
  address_proof_url: string;
}

export interface IStatus {
  status: string;
  reason?: string;
}

export function getAddress<T>(
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("firm_name"),
      key: "firm_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-1",
    },
    {
      name: t("address"),
      key: "address_line1",
      sort: true,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-4",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data["address_line1"] +
        ", " +
        data["address_line2"] +
        (data["address_line3"] !== null ? ", " + data["address_line3"] : ""),
    },
    {
      name: t("place"),
      key: "city",
      sort: true,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data["city"] + ", " + data["state_name"] + ", " + data["country_name"],
    },
    {
      name: t("zip_code"),
      key: "zip_code",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
    },
    {
      name: t("status"),
      key: "status",
      sort: false,
      type: TABLE_FIELD_TYPE.status,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
      statusColorClassPair: {
        [ADDRESS_STATUS.Pending]: "bg-warning-muted text-warning",
        [ADDRESS_STATUS.Rejected]: "bg-danger-muted text-danger",
        [ADDRESS_STATUS.Verified]: "bg-success-muted text-success",
      },
      statusLabelPair: {
        [ADDRESS_STATUS.Pending]: ADDRESS_STATUS_LABEL[ADDRESS_STATUS.Pending],
        [ADDRESS_STATUS.Rejected]:
          ADDRESS_STATUS_LABEL[ADDRESS_STATUS.Rejected],
        [ADDRESS_STATUS.Verified]:
          ADDRESS_STATUS_LABEL[ADDRESS_STATUS.Verified],
      },
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllAddress = async (
  setAddressList: TReactSetState<IAddress[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: string
) => {
  try {
    const address = await GET_ALL_ADDRESS(pagination, search, advanceSearch);
    if (address && address.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setAddressList(address.data.item);
      setPagination(address.data.pagination);
    } else {
      setAddressList([]);
      sweetAlertError({
        message: address.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllAddress");
  }
};

export const updateAddressStatus = async (
  id: string,
  value: ADDRESS_STATUS,
  rejectReason?: string
) => {
  try {
    const updateAddressStatusData = await UPDATE_ADDRESS_STATUS(id, {
      status: value,
      reason: rejectReason,
    });
    if (
      updateAddressStatusData &&
      updateAddressStatusData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(updateAddressStatusData.message);
      return true;
    } else {
      sweetAlertError({
        message:
          updateAddressStatusData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "updateAddressStatus");
    return false;
  }
};
